import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * To get the real type of the given value.
 */
const typeOf = value =>
  ({}.toString
    .call(value)
    .match(/\s([a-zA-Z]+)/)[1]
    .toLowerCase())

/**
 * To apply a bunch of formatters to the given value.
 */
export const makeFormat = (callbacks, value) =>
  callbacks.reduce((updatedValue, callback) => callback(updatedValue), value)

/**
 * To apply a bunch of validators to the given value.
 */
export const makeError = (callbacks, value) =>
  callbacks.reduce((message, callback) => {
    if (message !== '') {
      return message
    }

    return callback(value)
  }, '')

/**
 * To compare the two given arrays by length and content.
 */
export const enhancedArraysComparison = (source, target) => {
  const sortedSource = source.sort()
  const sortedTarget = target.sort()

  return source.length === target.length && sortedSource.length === sortedTarget.length
}

/**
 * To do a superficial comparison of the given values.
 */
export const shallowCompare = (source, target) => {
  if (typeOf(source) !== typeOf(target)) {
    return false
  }

  if (typeOf(source) === 'array') {
    return enhancedArraysComparison(source, target)
  }

  if (typeOf(source) === 'object') {
    return Object.keys(source).every(key => source[key] === target[key])
  }

  if (typeOf(source) === 'date') {
    return source.getTime() === target.getTime()
  }

  return source === target
}

/**
 * Custom hook to execute the error composition.
 */
export const useCheckError = (callbacks, callback) =>
  useCallback(
    updatedValue => callback(makeError(callbacks, updatedValue)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callbacks]
  )

/**
 * Custom hook to execute the format composition.
 */
export const useFormatter = callbacks => useCallback(value => makeFormat(callbacks, value), [callbacks])

/**
 * Custom hook to overwrite the current value.
 */
export const useOverwrite = (value, getter, callback) => {
  const isMountedRef = useRef(false)
  const overwriteRef = useRef(value)

  useEffect(() => {
    isMountedRef.current = true
  }, [])

  useEffect(() => {
    if (!isMountedRef.current) {
      return
    }

    const overwriteValue = overwriteRef.current
    const updatedValue = getter(value)

    overwriteRef.current = updatedValue

    if (
      updatedValue === undefined ||
      shallowCompare(overwriteValue, updatedValue) ||
      shallowCompare(value, updatedValue)
    ) {
      return
    }

    callback(updatedValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback])
}

/**
 * Custom hook to sync the error message regarding externalities.
 */
export const useSyncError = (dependencies, callback) => {
  const [mount, setMount] = useState(false)

  useEffect(() => {
    setMount(true)
  }, [])

  useEffect(() => {
    if (!mount) {
      return
    }

    callback()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
}
